import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  BookOpen,
  Coins,
  CreditCard,
  FileSearch,
  Gauge,
  History,
  Hotel,
  ImageUp,
  Inbox,
  Landmark,
  LayoutDashboard,
  KeyRound,
  ListChecks,
  LogOut,
  Menu,
  Moon,
  Plus,
  Receipt,
  Settings2,
  Sun,
  Users,
  X,
} from 'lucide-react';
import * as React from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { AnimatedNumber } from '@/components/motion';
import { Button } from '@/components/ui/button';
import { adminApi } from '@/lib/api/endpoints';
import { useAuth, useMe } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { cn, initials } from '@/lib/utils';
import { Backdrop, Logo } from './Logo';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
  badge?: number;
}

const CUSTOMER_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/checks/new', label: 'New check', icon: FileSearch },
  { to: '/hotel-booking', label: 'Hotel booking', icon: Hotel },
  { to: '/checks', label: 'My checks', icon: ListChecks, end: true },
  { to: '/coins/buy', label: 'Buy coins', icon: Coins },
  { to: '/coins/purchases', label: 'Purchases', icon: Receipt },
  { to: '/coins/history', label: 'Coin history', icon: History },
];

function useAdminNav(enabled: boolean): NavItem[] {
  const pending = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: adminApi.stats,
    enabled,
    refetchInterval: 60_000,
  });
  return [
    { to: '/admin', label: 'Overview', icon: Gauge, end: true },
    { to: '/admin/purchases', label: 'Review purchases', icon: Inbox, badge: pending.data?.pending_purchases },
    { to: '/admin/screenshots', label: 'Review screenshots', icon: ImageUp, badge: pending.data?.pending_screenshots },
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/admin/password-resets', label: 'Password resets', icon: KeyRound },
    { to: '/admin/checks', label: 'All checks', icon: ListChecks },
    { to: '/admin/ledger', label: 'Ledger', icon: BookOpen },
    { to: '/admin/activity', label: 'Activity log', icon: Activity },
    { to: '/admin/pricing', label: 'Pricing rules', icon: Settings2 },
    { to: '/admin/bank-accounts', label: 'Bank accounts', icon: Landmark },
  ];
}

function NavList({ items, onNavigate, layoutId }: { items: NavItem[]; onNavigate?: () => void; layoutId: string }) {
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive ? 'text-foreground' : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    layoutId={layoutId}
                    transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                    // While it slides between items it passes over other links; it must not take their clicks.
                    className="pointer-events-none absolute inset-0 rounded-lg bg-card shadow-sm ring-1 ring-border"
                  >
                    <span className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-brand-gradient" />
                  </motion.span>
                )}
                <item.icon className={cn('relative size-4 transition-transform group-hover:scale-110', isActive && 'text-primary-ink')} />
                <span className="relative flex-1">{item.label}</span>
                {!!item.badge && (
                  <span className="relative min-w-5 rounded-full bg-rose-500 px-1.5 text-center text-[11px] font-bold leading-5 text-white">
                    {item.badge}
                  </span>
                )}
              </>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function SidebarContent({ onNavigate, layoutId }: { onNavigate?: () => void; layoutId: string }) {
  const me = useMe();
  const { logout } = useAuth();
  const adminNav = useAdminNav(me.is_admin);
  const customerNav = me.is_admin
    ? CUSTOMER_NAV.filter(({ to }) => to !== '/coins/buy' && to !== '/coins/purchases')
    : CUSTOMER_NAV;
  return (
    <div className="flex h-full flex-col gap-6 p-4">
      <Link to="/" onClick={onNavigate} className="px-2 pt-1">
        <Logo />
      </Link>

      <Button asChild variant="brand" className="w-full">
        <Link to="/checks/new" onClick={onNavigate}>
          <Plus /> Check a visa
        </Link>
      </Button>

      <nav className="-mx-1 flex-1 space-y-6 overflow-y-auto px-1">
        <NavList items={customerNav} onNavigate={onNavigate} layoutId={layoutId} />
        {me.is_admin && (
          <div>
            <p className="mb-2 px-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70">Admin</p>
            <NavList items={adminNav} onNavigate={onNavigate} layoutId={layoutId} />
          </div>
        )}
      </nav>

      <div className="flex items-center gap-3 rounded-xl border bg-card/70 p-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-navy text-xs font-bold text-brand-sun">
          {initials(me.full_name || me.username)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{me.full_name || me.username}</p>
          <p className="truncate text-xs text-muted-foreground">@{me.username}</p>
        </div>
        <Button asChild variant="ghost" size="icon" className="size-8" title="Change password">
          <Link to="/account/password"><KeyRound /></Link>
        </Button>
        <Button variant="ghost" size="icon" className="size-8" onClick={logout} title="Sign out">
          <LogOut />
        </Button>
      </div>
    </div>
  );
}

function BalancePill() {
  const me = useMe();
  if (me.is_admin) return null;
  return (
    <Link
      to="/coins/buy"
      className="group flex items-center gap-2 rounded-full border bg-card/80 py-1 pl-1 pr-3 text-sm font-semibold shadow-sm backdrop-blur transition hover:border-amber-400/60 hover:shadow-md"
      title="Buy coins"
    >
      <span className="flex size-7 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-amber-950 shadow-inner transition-transform group-hover:rotate-12">
        <Coins className="size-4" />
      </span>
      <AnimatedNumber value={me.coin_balance} />
      <span className="hidden text-muted-foreground sm:inline">coins</span>
    </Link>
  );
}

function ThemeButton() {
  const [theme, toggle] = useTheme();
  return (
    <Button variant="outline" size="icon" className="size-9 rounded-full" onClick={toggle} title="Toggle theme">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={theme}
          initial={{ rotate: -90, opacity: 0 }}
          animate={{ rotate: 0, opacity: 1 }}
          exit={{ rotate: 90, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {theme === 'dark' ? <Moon className="size-4" /> : <Sun className="size-4" />}
        </motion.span>
      </AnimatePresence>
    </Button>
  );
}

export function AppShell() {
  const [open, setOpen] = React.useState(false);
  const me = useMe();
  const location = useLocation();

  React.useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className="min-h-screen">
      <Backdrop />

      {/* Desktop sidebar */}
      <aside className="glass fixed inset-y-0 left-0 z-30 hidden w-64 border-y-0 border-l-0 lg:block">
        <SidebarContent layoutId="nav-desktop" />
      </aside>

      {/* Mobile drawer. Always mounted and driven by `open`, so a closing animation that stalls
          can never leave the overlay over the page swallowing every tap. */}
      <motion.div
        aria-hidden
        className={cn('fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-sm lg:hidden', !open && 'pointer-events-none')}
        initial={false}
        animate={{ opacity: open ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        onClick={() => setOpen(false)}
      />
      <motion.aside
        className={cn('fixed inset-y-0 left-0 z-50 w-72 border-r bg-background lg:hidden', !open && 'pointer-events-none')}
        initial={false}
        animate={{ x: open ? 0 : '-100%' }}
        transition={{ type: 'spring', stiffness: 380, damping: 38 }}
        aria-hidden={!open}
        {...(open ? {} : { inert: '' })}
      >
        <Button variant="ghost" size="icon" className="absolute right-3 top-4 size-9" onClick={() => setOpen(false)}>
          <X />
        </Button>
        <SidebarContent onNavigate={() => setOpen(false)} layoutId="nav-mobile" />
      </motion.aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 border-b border-transparent bg-background/60 backdrop-blur-xl supports-[backdrop-filter]:bg-background/40">
          <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4 sm:px-6">
            <Button variant="ghost" size="icon" className="size-9 lg:hidden" onClick={() => setOpen(true)}>
              <Menu />
            </Button>
            <Link to="/" className="lg:hidden">
              <Logo compact />
            </Link>
            <div className="flex-1" />
            <BalancePill />
            <ThemeButton />
            {!me.is_admin && (
              <Button asChild variant="outline" size="icon" className="hidden size-9 rounded-full sm:inline-flex" title="Purchases">
                <Link to="/coins/purchases">
                  <CreditCard className="size-4" />
                </Link>
              </Button>
            )}
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-4 pb-16 pt-4 sm:px-6">
          {/* Enter-only: waiting for the old page to animate out kept the new one unclickable. */}
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  eyebrow?: string;
}) {
  return (
    <div className="mb-6 flex flex-col gap-4 pt-2 sm:flex-row sm:items-end sm:justify-between">
      <div className="space-y-1.5">
        {eyebrow && <p className="text-xs font-bold uppercase tracking-widest text-primary-ink">{eyebrow}</p>}
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h1>
        {description && <p className="max-w-2xl text-sm text-muted-foreground sm:text-base">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
